import { useCallback, useMemo } from "react";
import { FilterOption } from "../types";

export const KNOWN_SIZE_FORMATS = ["EU", "US", "UK", "International Size"];

export const useSizeFilterHelpers = () => {
  const getNodeKey = useCallback((node: any): string | null => {
    if (!node) return null;
    return node.key || String(node.label || node.defaultValue || "");
  }, []);

  const flattenLeafOptions = useCallback((node: any): FilterOption[] => {
    if (!node) return [];
    const children = node?.children;
    if (!Array.isArray(children) || children.length === 0) return [];
    const hasNestedChildren = children.some((child: any) =>
      Array.isArray(child?.children)
    );
    if (!hasNestedChildren) return children as FilterOption[];
    return children.flatMap((child: any) => flattenLeafOptions(child));
  }, []);

  const getUniqueOptionsByKey = useCallback(
    (options: any[]): FilterOption[] => {
      const seenKeys = new Set<string>();
      const uniqueOptions: FilterOption[] = [];
      (options || []).forEach((option: any) => {
        const key = typeof option?.key === "string" ? option.key : null;
        if (!key || seenKeys.has(key)) return;
        seenKeys.add(key);
        uniqueOptions.push(option);
      });
      return uniqueOptions;
    },
    []
  );

  const filterOptionsWithValidLabel = useCallback(
    (options: any[]): FilterOption[] => {
      return (options || []).filter((option: any) => {
        const label = option?.label;
        return typeof label === "string" && label.trim().length > 0;
      });
    },
    []
  );

  const detectSizeFormat = useCallback(
    (...sources: Array<string | null | undefined>): string | null => {
      for (const source of sources) {
        if (!source) continue;
        const sourceString = String(source).trim();
        if (!sourceString) continue;

        if (KNOWN_SIZE_FORMATS.includes(sourceString)) return sourceString;

        const words = sourceString.split(/\s+/);
        const lastWord = words[words.length - 1];
        if (KNOWN_SIZE_FORMATS.includes(lastWord)) return lastWord;

        const keyMatch = sourceString.toUpperCase().match(/\b(US|UK|EU)\b/);
        if (keyMatch) return keyMatch[1];

        const normalizedSource = sourceString.replace(/-/g, " ").toUpperCase();
        const foundFormat = KNOWN_SIZE_FORMATS.find(format =>
          normalizedSource.includes(format.toUpperCase())
        );
        if (foundFormat) return foundFormat;
      }
      return null;
    },
    []
  );

  const getOptionSizeFormat = useCallback(
    (option?: any): string | null =>
      detectSizeFormat(option?.key, option?.defaultValue, option?.label),
    [detectSizeFormat]
  );

  const collectAvailableFormatsFromGroup = useCallback(
    (group: any): string[] => {
      const foundFormats = new Set<string>();
      const walkNode = (node: any) => {
        if (!node) return;
        const format = detectSizeFormat(
          node?.key,
          node?.defaultValue,
          node?.label
        );
        if (format) foundFormats.add(format);
        if (Array.isArray(node.children)) {
          node.children.forEach(walkNode);
        }
      };
      walkNode(group);
      // Include raw keys of direct children that have no recognized format
      // (e.g. "Age", "Length" dropdown options that don't match known size format names)
      if (Array.isArray(group?.children)) {
        group.children.forEach((child: any) => {
          const childFormat = detectSizeFormat(
            child?.key,
            child?.defaultValue,
            child?.label
          );
          if (!childFormat && child?.key) foundFormats.add(child.key);
        });
      }
      return Array.from(foundFormats);
    },
    [detectSizeFormat]
  );

  const findGroupContainingSelectedOption = useCallback(
    (
      groupsList: any[],
      selectedKeys: string[]
    ): { groupKey: string | null; format: string | null } => {
      if (!selectedKeys.length) return { groupKey: null, format: null };
      const selectedKeySet = new Set(selectedKeys);
      for (const group of groupsList || []) {
        const groupKey = getNodeKey(group) || "";
        const leafOptions = flattenLeafOptions(group);
        const matchedOption = leafOptions.find(
          (leaf: any) => leaf?.key && selectedKeySet.has(leaf.key)
        );
        if (matchedOption) {
          const format = detectSizeFormat(
            matchedOption.key,
            matchedOption.defaultValue,
            matchedOption.label,
            groupKey
          );
          return { groupKey, format };
        }
      }
      return { groupKey: null, format: null };
    },
    [flattenLeafOptions, detectSizeFormat, getNodeKey]
  );

  const findFirstSelectedOptionPath = useCallback(
    (
      groupsList: any[],
      selectedKeySet: Set<string>
    ): {
      groupKey: string | null;
      tabKey?: string | null;
      subgroupKey?: string | null;
      format?: string | null;
    } | null => {
      const walkNode = (
        node: any,
        context: {
          groupKey: string | null;
          tabKey?: string | null;
          subgroupKey?: string | null;
        }
      ): any => {
        if (!node) return null;
        const children = node.children;
        if (!Array.isArray(children) || children.length === 0) {
          const key = typeof node?.key === "string" ? node.key : null;
          if (key && selectedKeySet.has(key)) {
            return {
              ...context,
              format: detectSizeFormat(
                key,
                node.defaultValue,
                node.label,
                context.subgroupKey,
                context.tabKey,
                context.groupKey
              )
            };
          }
          return null;
        }

        const hasNestedChildren = children.some((child: any) =>
          Array.isArray(child?.children)
        );
        if (!hasNestedChildren) {
          for (const child of children) {
            const key = typeof child?.key === "string" ? child.key : null;
            if (key && selectedKeySet.has(key)) {
              return {
                ...context,
                format: detectSizeFormat(
                  key,
                  child.defaultValue,
                  child.label,
                  context.subgroupKey,
                  context.tabKey,
                  context.groupKey
                )
              };
            }
          }
          return null;
        }

        for (const child of children) {
          const nextContext = { ...context };
          const childKey = getNodeKey(child);
          if (!context.tabKey) nextContext.tabKey = childKey;
          else if (!context.subgroupKey) nextContext.subgroupKey = childKey;
          const result = walkNode(child, nextContext);
          if (result) return result;
        }
        return null;
      };

      for (const group of groupsList || []) {
        const groupKey = getNodeKey(group);
        const result = walkNode(group, { groupKey });
        if (result) return result;
      }
      return null;
    },
    [getNodeKey, detectSizeFormat]
  );

  const getDeepestChildrenOptions = useCallback(
    (group: any): FilterOption[] => {
      if (!group?.children) return [];
      if (group.children[0]?.children) {
        return group.children.flatMap((child: any) =>
          getDeepestChildrenOptions(child)
        );
      }
      return group.children;
    },
    []
  );

  return {
    getNodeKey,
    flattenLeafOptions,
    getUniqueOptionsByKey,
    filterOptionsWithValidLabel,
    getOptionSizeFormat,
    collectAvailableFormatsFromGroup,
    findGroupContainingSelectedOption,
    findFirstSelectedOptionPath,
    getDeepestChildrenOptions
  };
};
